import React from 'react';
import { Alert, Button, Box, Typography, LinearProgress } from '@mui/material';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const LimitReachedBanner = ({
  limitType,
  current,
  limit,
  onUpgrade,
  severity = 'warning',
  showProgress = true
}) => {
  // null limit means unlimited — never show the banner
  if (limit === null || limit === undefined) return null;

  const percentage = limit > 0 ? (current / limit) * 100 : 0;

  const limitLabels = {
    projects: 'Projects',
    surveys: 'Surveys',
    team_members: 'Team Members',
    social_posts: 'Social Posts',
    ai_generations: 'AI Generations'
  };

  const label = limitLabels[limitType] || 'Resources';
  const isAtLimit = current >= limit;
  const isNearLimit = percentage >= 80 && !isAtLimit;

  if (!isNearLimit && !isAtLimit) {
    return null; // Don't show banner if not near limit
  }

  const getSeverity = () => {
    if (isAtLimit) return 'error';
    if (isNearLimit) return 'warning';
    return 'info';
  };

  const getMessage = () => {
    if (isAtLimit) {
      return `You've reached your ${label.toLowerCase()} limit (${current}/${limit}). Upgrade to create more.`;
    }
    if (isNearLimit) {
      return `You're approaching your ${label.toLowerCase()} limit (${current}/${limit}). Consider upgrading soon.`;
    }
  };

  return (
    <Alert
      severity={getSeverity()}
      icon={<WarningAmberIcon />}
      action={
        <Button
          color="inherit"
          size="small"
          startIcon={<UpgradeIcon />}
          onClick={onUpgrade}
        >
          Upgrade
        </Button>
      }
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="body2" fontWeight="bold">
          {isAtLimit ? `${label} Limit Reached` : `Approaching ${label} Limit`}
        </Typography>
        <Typography variant="body2">
          {getMessage()}
        </Typography>
        {showProgress && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(percentage, 100)} 
              color={isAtLimit ? 'error' : 'warning'}
              sx={{ height: 6, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {percentage.toFixed(0)}% used
            </Typography>
          </Box>
        )}
      </Box>
    </Alert>
  );
};

export default LimitReachedBanner;
